import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { buildDefaultGraph } from '@/components/SEO';

/**
 * Global structured-data fallback. Guarantees EVERY route emits the stacked
 * JSON-LD @graph (Organization + WebSite + WebPage + BreadcrumbList), even
 * pages that manage their own <title>/<meta> via react-helmet directly and
 * therefore never render the shared <SEO> component. Rendered once inside the
 * Router. If a page's <SEO> already emitted the graph, this stays out of the way.
 */
const StructuredData = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Defer so page-level SEO/Helmet effects run first and set the final
    // title / description / canonical before we read them.
    const t = setTimeout(() => {
      // Clear only our own previous global block (leave page-managed <SEO> ones).
      document.querySelectorAll('script[data-seo-schema][data-schema-source="global"]').forEach((s) => s.remove());
      // If a page's <SEO> already produced the stacked graph, do nothing.
      if (document.querySelector('script[data-seo-schema]:not([data-schema-source="global"])')) return;

      const canonEl = document.querySelector('link[rel="canonical"]');
      const canonical = (canonEl && canonEl.href) ||
        ('https://web-techservices.com' + ((pathname || '/').replace(/\/+$/, '') || '/'));
      const title = document.title || 'Web-TechServices';
      const descEl = document.querySelector('meta[name="description"]');
      const description = (descEl && descEl.getAttribute('content')) || '';
      const ogImgEl = document.querySelector('meta[property="og:image"]');
      const ogImage = ogImgEl && ogImgEl.getAttribute('content');

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.setAttribute('data-schema-source', 'global');
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': buildDefaultGraph(title, description, canonical, ogImage),
      });
      document.head.appendChild(script);
    }, 150);

    return () => clearTimeout(t);
  }, [pathname]);

  return null;
};

export default StructuredData;
